/**
 * CreateLectureForm — the form body of the "Create New Lecture" dialog.
 *
 * Extracted from LecturesPage.js (Feb 2026 refactor) so the page-level
 * component stays focused on data orchestration. The dialog shell
 * (<Dialog open ...>) still lives in the parent; this component just
 * owns the DialogContent body so it's easy to test in isolation.
 */
import { Loader2, Sparkles } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import SubjectSelectWithInlineCreate from '../SubjectSelectWithInlineCreate';
import CloudBackupHint from './CloudBackupHint';

const CreateLectureForm = ({
  formData,
  setFormData,
  subjects,
  setSubjects,
  autoGenerate,
  setAutoGenerate,
  submitting,
  onSubmit,
  onCancel,
}) => (
  <DialogContent
    className="sm:max-w-2xl bg-[var(--isp-glass)] border-[var(--isp-glass-border)] text-[var(--isp-heading)]  max-h-[90vh] overflow-y-auto"
    data-testid="lecture-dialog"
  >
    <DialogHeader>
      <DialogTitle className="text-2xl font-bold text-[var(--isp-heading)]">Create New Lecture</DialogTitle>
    </DialogHeader>
    <form onSubmit={onSubmit} className="space-y-4 mt-4">
      <div className="space-y-2">
        <Label htmlFor="subject_id" className="text-[var(--isp-body)]">Subject *</Label>
        <div data-testid="subject-select">
          <SubjectSelectWithInlineCreate
            value={formData.subject_id}
            onChange={(v) => setFormData({ ...formData, subject_id: v })}
            subjects={subjects}
            onSubjectCreated={(created) => setSubjects((prev) => [created, ...prev])}
            testidPrefix="manual-subject-select"
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="title" className="text-[var(--isp-body)]">Lecture Title *</Label>
        <Input
          id="title"
          placeholder="e.g., Introduction to Calculus"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
          className="rounded-xl bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] placeholder-[var(--isp-muted)]"
          data-testid="lecture-title-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="topic" className="text-[var(--isp-body)]">Topic</Label>
        <Input
          id="topic"
          placeholder="e.g., Derivatives and Limits"
          value={formData.topic}
          onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
          className="rounded-xl bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] placeholder-[var(--isp-muted)]"
          data-testid="lecture-topic-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes" className="text-[var(--isp-body)]">Lecture Notes</Label>
        <Textarea
          id="notes"
          placeholder="Add your lecture notes here..."
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={6}
          className="rounded-xl bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] placeholder-[var(--isp-muted)] resize-none"
          data-testid="lecture-notes-textarea"
        />
      </div>

      <div className="flex items-center gap-2 p-4 bg-[#002FA7]/10 rounded-xl border border-[#002FA7]/20">
        <Sparkles className="w-5 h-5 text-[#002FA7]" aria-hidden />
        <p className="text-sm text-[var(--isp-body)]">AI will generate summary, key points, and learning materials</p>
      </div>

      {/* iter191 — contextual cloud-storage nudge inside the save modal */}
      <CloudBackupHint />

      <label className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 cursor-pointer" data-testid="auto-generate-toggle">
        <input
          type="checkbox"
          checked={autoGenerate}
          onChange={(e) => setAutoGenerate(e.target.checked)}
          className="w-4 h-4 rounded accent-amber-500"
        />
        <div>
          <p className="text-sm font-medium text-amber-300">Auto-generate flashcards</p>
          <p className="text-xs text-amber-300/60">Create study cards automatically after saving</p>
        </div>
      </label>

      <label className="flex items-center gap-3 p-3 rounded-xl bg-[#002FA7]/10 border border-[#002FA7]/20 cursor-pointer" data-testid="make-public-toggle">
        <input
          type="checkbox"
          checked={formData.is_public}
          onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
          className="w-4 h-4 rounded accent-[#002FA7]"
        />
        <div>
          <p className="text-sm font-medium text-[#002FA7]">Make Public</p>
          <p className="text-xs text-[#002FA7]/60">Share to the discovery feed for others to find</p>
        </div>
      </label>

      <div className="flex gap-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)]"
          data-testid="cancel-lecture-button"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={submitting || !formData.subject_id || !formData.title}
          className="flex-1 rounded-xl isp-btn-primary"
          data-testid="submit-lecture-button"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden />
              Creating...
            </>
          ) : (
            'Create Lecture'
          )}
        </Button>
      </div>
    </form>
  </DialogContent>
);

export default CreateLectureForm;
